import { useMutation, useQuery } from "@apollo/client"
import { ArrowForward } from "@mui/icons-material"
import {
    Box,
    CardActionArea,
    CircularProgress,
    Divider,
    InputAdornment,
    Stack,
    TextField,
    Typography,
} from "@mui/material"
import React, { useCallback, useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import ConfirmationDialog from "../../components/ConfirmationDialog"
import { CenterCircular } from "../../components/Loading"
import ShiftContext from "../../contexts/ShiftContext"
import SiteContext from "../../contexts/SiteContext"
import { UPDATE_ODOMETER_MUTATION } from "../../entities/vehicle/graphql/mutations"
import { GET_VEHICLE_QUERY } from "../../entities/vehicle/graphql/queries"
import NativeLayerContext from "./../../contexts/NativeLayerContext"
import {
    EFunctionalityOverrides,
    ENativeLayerOutgoingEventType,
} from "./../../utils/nativeLayerCommunication/interfaces"
import { sendNativeLayerEvent } from "./../../utils/nativeLayerCommunication/sendNativeLayerEvent"
import { useAuth0 } from "@auth0/auth0-react"
import { IVehicle } from "../../entities/vehicle/interface"

const Vehicle = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const { logout } = useAuth0()

    const nativeLayerContext = useContext(NativeLayerContext)
    const shouldLogout = location.state?.logout
    const [isOdometer] = useState<boolean>(location.state?.odometer)
    const [vehicle, setVehicle] = useState({} as IVehicle)

    const context = useContext(ShiftContext)
    const siteContext = useContext(SiteContext)

    // Handle context missing
    if (!context) {
        throw new Error("ShiftContext is not provided")
    }

    const { loading } = useQuery(GET_VEHICLE_QUERY, {
        variables: { id: context.vehicleId },
        fetchPolicy: "cache-and-network",
        skip: !context.vehicleId,
        onCompleted: (data) => {
            isOdometer && setOdometer(data.vehicle.odometer)
            context.setVehicle(data.vehicle)
            setVehicle(data.vehicle)
        },
    })

    const [updateOdometer, { loading: mutationLoading }] = useMutation(UPDATE_ODOMETER_MUTATION)

    const [odometer, setOdometer] = useState<number | null>(context.vehicle?.odometer ?? 0)
    const [errorMessage, setErrorMessage] = useState("")
    const [showConfirmDialog, setShowConfirmDialog] = useState(false)
    const [confirmNoFormDialog, setConfirmNoFormDialog] = useState(false)

    // Handle the odometer change input
    const handleOdometerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value

        // Allow empty input or numeric values only
        if (value === "" || !isNaN(Number(value))) {
            setOdometer(Number(value))
        }
    }

    //Handle Navigation
    const handleNavigateToForm = () => {
        const formId = context?.vehicle?.form?._id ?? null

        if (!formId) {
            setConfirmNoFormDialog(true)
        } else {
            // Navigate with formId as state
            navigate("/form", { state: { formId: formId, entity: "Vehicle", entityId: context.vehicleId } })
        }
    }

    // Utility function for validation
    const validateOdometer = useCallback(() => {
        const currentOdometer = context?.vehicle?.odometer ?? 0

        if (odometer) {
            // Validate odometer values
            if (odometer < currentOdometer) {
                setErrorMessage(t("vehicles.mobile.error.odometerMismatch"))
                return false
            }

            if (odometer - currentOdometer > 3000) {
                setShowConfirmDialog(true)
                return false
            }

            return true
        }
    }, [odometer, t])

    const newOdometerValue = useCallback(() => {
        const currentOdometer = context?.vehicle?.odometer ?? 0
        if (odometer === currentOdometer) {
            return false
        }
        return true
    }, [odometer])

    // Handle odometer update
    const handleOdometerUpdate = async () => {
        if (validateOdometer()) {
            try {
                if (newOdometerValue()) {
                    await updateOdometer({
                        variables: { id: context.vehicleId, record: { odometer: odometer } },
                        onCompleted: (data) => {
                            if (data?.updateVehicle?.record) {
                                const updatedOdometer = data.updateVehicle?.record.odometer
                                // Update context vehicle
                                context.setVehicle({ ...vehicle, odometer: updatedOdometer })
                                setVehicle({ ...vehicle, odometer: updatedOdometer })
                            }
                        },
                    })
                }
                if (shouldLogout) {
                    const completedJobGroups = localStorage.getItem("completedJobGroups")
                    localStorage.clear()
                    if (completedJobGroups) {
                        localStorage.setItem("completedJobGroups", completedJobGroups)
                    }
                    if (nativeLayerContext.nativeFunctionalityOverrides?.includes(EFunctionalityOverrides.AUTH)) {
                        sendNativeLayerEvent({ type: ENativeLayerOutgoingEventType.LOGOUT })
                    } else {
                        logout({ logoutParams: { returnTo: window.location.origin } })
                    }
                } else {
                    // Optional: Navigate after successful update
                    handleNavigateToForm()
                }
            } catch (err) {
                console.error("Error updating odometer:", err)
            }
        }
    }

    // Handle confirmation
    const handleConfirm = async () => {
        setShowConfirmDialog(false)
        try {
            await updateOdometer({
                variables: { id: context.vehicleId, record: { odometer: odometer } },
            })
            handleNavigateToForm()
        } catch (err) {
            console.error("Error updating odometer:", err)
        }
    }

    // Handle cancel
    const handleCancel = () => {
        setShowConfirmDialog(false)
    }

    if (loading) {
        return <CenterCircular />
    }

    return isOdometer ? (
        <Box
            sx={{
                position: "absolute",
                left: "6%",
                bottom: "10%",
                borderRadius: 6,
                width: "85%",
                bgcolor: "primary.main",
                boxShadow: 24,
                p: 4,
            }}
        >
            <Typography color="text.secondary" variant="h6" align="center" fontWeight="bold">
                {t("vehicles.mobile.details.enterOdometerReading", { vehicle: context?.vehicle?.identifier })}
            </Typography>

            <Divider
                sx={{
                    width: "100%",
                    my: 2,
                    borderBottomWidth: 1.5,
                    borderStyle: "dashed",
                    borderColor: "text.secondary",
                }}
            />

            <TextField
                id="odometer"
                variant="filled"
                placeholder={t("vehicles.mobile.details.odometerPlaceholder")}
                // type="number"
                fullWidth
                value={odometer}
                onChange={handleOdometerChange}
                error={!!errorMessage} // Highlight the TextField with an error if errorMessage exists
                helperText={errorMessage} // Display the error message below the input
                onFocus={() => setErrorMessage("")} // Clear error message when the TextField is focused
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end" onClick={handleOdometerUpdate} style={{ cursor: "pointer" }}>
                            {mutationLoading ? <CircularProgress color="secondary" size={24} /> : <ArrowForward />}
                        </InputAdornment>
                    ),
                }}
            />

            {/* High Odometer Confirmation */}
            <ConfirmationDialog
                open={showConfirmDialog}
                title={t("vehicles.mobile.details.highOdometerTitle")}
                content={t("vehicles.mobile.details.highOdometerContent")}
                actions={{
                    confirmText: t("common.yes"),
                    cancelText: t("common.no"),
                    onConfirm: handleConfirm,
                    onCancel: handleCancel,
                }}
                onClose={handleCancel}
            />
            <ConfirmationDialog
                open={confirmNoFormDialog}
                title={t("vehicles.mobile.form.noFormDialogTitle")}
                content={t("vehicles.mobile.form.noFormDialogContent")}
                actions={{
                    confirmText: t("common.continue"),
                    cancelText: t("common.back"),
                    onConfirm: () => {
                        if (siteContext?.site?.tier === "TIER1") {
                            // If the site has the tracking feature, navigate to the tracking page
                            navigate("/tracking")
                        } else {
                            navigate("/job-listing")
                        }
                    },
                    onCancel: () => setConfirmNoFormDialog(false),
                }}
                onClose={handleCancel}
            />
        </Box>
    ) : (
        <Box
            width="100%"
            paddingTop="3vh" // Adjusting top padding for better alignment
            display="flex"
            flexDirection="column"
            alignItems="center"
            sx={{ height: "100vh" }} // Dark background for contrast
        >
            <Stack sx={{ pl: 1, pr: 3, width: "90%" }} spacing={4}>
                <Typography
                    color="text.primary"
                    variant="h6"
                    sx={{
                        textAlign: "center",
                        mb: 3,
                    }}
                >
                    {t("vehicles.mobile.title")}
                </Typography>
                <Box
                    sx={{
                        width: "100%",
                        marginTop: { xs: 3, sm: 4, md: 5 },
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "right",
                    }}
                >
                    <Box
                        width="100%"
                        display="flex"
                        flexDirection="row"
                        borderRadius={5}
                        sx={{ bgcolor: "primary.main" }} // Assign color based on index
                        p={2}
                    >
                        <Box width="90%" position="relative" marginY={1}>
                            <CardActionArea>
                                <Box padding={2}>
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="white"
                                            sx={{ fontWeight: "medium", fontSize: "1.1rem" }}
                                        >
                                            {vehicle?.identifier}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {vehicle?.capacity}T
                                        </Typography>
                                    </Box>
                                    <Divider
                                        sx={{
                                            width: "20%",
                                            borderStyle: "solid",
                                            marginTop: "6%",
                                            marginBottom: "6%",
                                            borderColor: "white",
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            color="white"
                                            sx={{ fontWeight: "medium", fontSize: "1.1rem" }}
                                        >
                                            {vehicle?.licencePlate}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {vehicle?.engineNumber}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardActionArea>
                        </Box>
                    </Box>
                </Box>
            </Stack>
        </Box>
    )
}

export default Vehicle
